<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  processVariable,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  UPLOAD_CONFIG,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import WhatsAppTemplatePreview from './WhatsAppTemplatePreview.vue';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  sampleContact: {
    type: Object,
    default: null,
  },
  showPreview: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const PAYMENT_BUTTON_TYPES = [
  'order_details',
  'payment_request',
  'payment_request_cta',
  'payment_request_button',
];
const PIX_KEY_TYPES = ['CNPJ', 'CPF', 'EMAIL', 'PHONE', 'EVP'];

const isPaymentButton = button => {
  return PAYMENT_BUTTON_TYPES.includes(button?.type);
};

const paymentMethodOptions = computed(() => [
  {
    value: 'pix_dynamic_code',
    label: t('WHATSAPP_TEMPLATES.PARSER.PIX_DYNAMIC_CODE'),
  },
  {
    value: 'boleto',
    label: t('WHATSAPP_TEMPLATES.PARSER.BOLETO'),
  },
  {
    value: 'payment_link',
    label: t('WHATSAPP_TEMPLATES.PARSER.PAYMENT_LINK'),
  },
]);

const isPaymentButtonInvalid = button => {
  if (button.type === 'order_details') {
    if (!button.reference_id || !button.total_amount) return true;
  }

  const paymentMethod = button.payment_method || 'pix_dynamic_code';

  if (paymentMethod === 'boleto') {
    return !button.boleto_digitable_line;
  }
  if (paymentMethod === 'payment_link') {
    return !button.payment_link_uri;
  }
  return (
    !button.pix_code ||
    !button.pix_merchant_name ||
    !button.pix_key ||
    !button.pix_key_type
  );
};

const paymentButtonValueField = button => {
  const paymentMethod = button?.payment_method || 'pix_dynamic_code';
  if (paymentMethod === 'boleto') return 'boleto_digitable_line';
  if (paymentMethod === 'payment_link') return 'payment_link_uri';
  return 'pix_code';
};

const contactAttributes = useMapGetter('attributes/getContactAttributes');

const variableOptions = computed(() => {
  const standard = [
    { id: 'name', name: t('CONTACT_FORM.FORM.NAME.LABEL') },
    {
      id: 'first_name',
      name: t('CONTACT_PANEL.CUSTOM_ATTRIBUTES.FIRST_NAME', 'Primeiro Nome'),
    },
    { id: 'email', name: t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') },
    { id: 'phone_number', name: t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL') },
    { id: 'company_name', name: t('CONTACT_FORM.FORM.COMPANY_NAME.LABEL') },
  ].map(attr => ({
    label: attr.name,
    value: `{{contact.${attr.id}}}`,
  }));

  const custom = (contactAttributes.value || []).map(attr => ({
    label: attr.attributeDisplayName,
    value: `{{contact.custom_attributes.${attr.attributeKey}}}`,
  }));

  return [
    { label: t('CONTACT_PANEL.CUSTOM_ATTRIBUTES.STANDARD'), options: standard },
    { label: t('CONTACT_PANEL.CUSTOM_ATTRIBUTES.CUSTOM'), options: custom },
  ];
});

const variableFieldRowClass =
  'grid grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,1fr)_13rem] sm:items-start';
const variableSelectWrapperClass = 'relative min-w-0 w-full';
const variableSelectClass =
  'reset-base no-margin !mb-0 block h-10 w-full appearance-none rounded-lg border-0 bg-n-alpha-black2 px-3 py-2.5 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak transition-colors duration-[0.25s] ease-[ease-in-out] [background-image:none] placeholder:text-n-slate-10 hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-weak dark:hover:outline-n-slate-6 dark:focus:outline-n-brand ltr:pr-9 rtl:pl-9';
const variableSelectIconClass =
  'i-lucide-chevron-down pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 ltr:right-3 rtl:left-3';

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const handleVariableSelect = (event, type, indexOrKey) => {
  const value = event.target.value;
  if (!value) return;

  if (type === 'header' && indexOrKey) {
    processedParams.value.header[indexOrKey] =
      (processedParams.value.header[indexOrKey] || '') + value;
  } else if (type === 'header') {
    updateMediaUrl((processedParams.value.header?.media_url || '') + value);
  } else if (type === 'body') {
    processedParams.value.body[indexOrKey] =
      (processedParams.value.body[indexOrKey] || '') + value;
  } else if (type === 'button') {
    const button = processedParams.value.buttons[indexOrKey];
    const field = isPaymentButton(button)
      ? paymentButtonValueField(button)
      : 'parameter';
    button[field] = (button[field] || '') + value;
  }

  // Reset the select element after a tick to let Vue process the v-model updates
  setTimeout(() => {
    event.target.value = '';
  }, 0);
};

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const headerText = computed(() => {
  if (headerComponent.value?.format !== 'TEXT') return '';
  return headerComponent.value?.text || '';
});

const extractVariableKeys = text =>
  Array.from(
    new Set(
      [...(text?.match(/{{([^}]+)}}/g) || [])].map(variable =>
        processVariable(variable)
      )
    )
  );

const headerVariableKeys = computed(() =>
  extractVariableKeys(headerText.value)
);

const bodyVariableKeys = computed(() => extractVariableKeys(bodyText.value));

const hasMediaHeader = computed(() => {
  const format = headerComponent.value?.format;
  return MEDIA_FORMATS.includes(format);
});

const hasBuiltInMedia = computed(() => {
  return !!headerComponent.value?.example?.header_handle?.[0];
});

const builtInMediaUrl = computed(() => {
  return headerComponent.value?.example?.header_handle?.[0] || '';
});

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasHeaderVariables = computed(() => headerVariableKeys.value.length > 0);

const hasBodyVariables = computed(() => bodyVariableKeys.value.length > 0);

const hasVariables = computed(() => {
  return hasHeaderVariables.value || hasBodyVariables.value;
});

const renderedHeader = computed(() => {
  return replaceTemplateVariables(
    headerText.value,
    processedParams.value,
    'header'
  );
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value, {
    sampleContact: props.sampleContact,
  });
});

const isFormInvalid = computed(() => {
  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasHeaderVariables.value) {
    const hasEmptyHeaderVariable = headerVariableKeys.value.some(
      key => !processedParams.value.header?.[key]
    );
    if (hasEmptyHeaderVariable) return true;
  }

  if (hasBodyVariables.value) {
    const hasEmptyBodyVariable = bodyVariableKeys.value.some(
      key => !processedParams.value.body?.[key]
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => {
        if (!button) return false;
        if (isPaymentButton(button)) return isPaymentButtonInvalid(button);
        if (button.optional && !button.parameter) return false;
        return !button.parameter;
      }
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );

  if (hasBuiltInMedia.value && processedParams.value.header) {
    processedParams.value.header.media_url = builtInMediaUrl.value;
  }
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

// File upload state for media headers
const fileInputRef = ref(null);
const isUploading = ref(false);
const uploadError = ref('');
const uploadedPreview = ref(null);

const acceptTypes = computed(() => {
  const format = headerComponent.value?.format;
  return UPLOAD_CONFIG[format]?.accept || '';
});

const triggerFileUpload = () => {
  fileInputRef.value?.click();
};

const handleFileSelected = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  isUploading.value = true;
  uploadError.value = '';

  try {
    const { fileUrl } = await uploadFile(file);
    updateMediaUrl(fileUrl);
    uploadedPreview.value = {
      url: fileUrl,
      name: file.name,
      type: file.type,
    };

    if (isDocumentTemplate.value) {
      updateMediaName(file.name);
    }
  } catch {
    uploadError.value = t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_ERROR');
  } finally {
    isUploading.value = false;
    if (fileInputRef.value) fileInputRef.value.value = '';
  }
};

const removeUploadedMedia = () => {
  updateMediaUrl('');
  uploadedPreview.value = null;
  uploadError.value = '';
  if (isDocumentTemplate.value) {
    updateMediaName('');
  }
};

const getFinalProcessedParams = () => {
  return JSON.parse(JSON.stringify(processedParams.value));
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid || isFormInvalid.value) return;

  const { name, category, language, namespace } = props.template;

  const finalProcessedParams = getFinalProcessedParams();

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: finalProcessedParams,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  getFinalProcessedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  bodyText,
  headerText,
  renderedHeader,
  renderedTemplate,
  isFormInvalid,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div
        class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
      >
        <h3 class="min-w-0 text-sm font-medium break-words text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs shrink-0 text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div
            v-if="renderedHeader"
            class="mb-1 text-sm font-semibold whitespace-pre-wrap break-words text-n-slate-12"
          >
            {{ renderedHeader }}
          </div>
          <div
            class="text-sm leading-6 whitespace-pre-wrap break-words text-n-slate-12"
          >
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>
    <!-- Live WhatsApp preview -->
    <WhatsAppTemplatePreview
      v-if="showPreview"
      :template="template"
      :processed-params="processedParams"
      :sample-contact="sampleContact"
      class="mb-4"
    />

    <div v-if="hasVariables || hasMediaHeader">
      <!-- Header Variables Section -->
      <div v-if="hasHeaderVariables && processedParams.header" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.HEADER_VARIABLES_LABEL') }}
        </p>
        <div
          v-for="key in headerVariableKeys"
          :key="`header-${key}`"
          class="mb-2.5"
          :class="variableFieldRowClass"
        >
          <Input
            v-model="processedParams.header[key]"
            type="text"
            class="min-w-0"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
          <div :class="variableSelectWrapperClass">
            <select
              :class="variableSelectClass"
              @change="handleVariableSelect($event, 'header', key)"
            >
              <option value="">
                {{ t('VARIABLES.DROPDOWN.LABEL', 'Add variable') }}
              </option>
              <optgroup
                v-for="group in variableOptions"
                :key="group.label"
                :label="group.label"
              >
                <option
                  v-for="opt in group.options"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </optgroup>
            </select>
            <span :class="variableSelectIconClass" />
          </div>
        </div>
      </div>

      <div v-if="hasMediaHeader && !hasBuiltInMedia" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>

        <input
          ref="fileInputRef"
          type="file"
          :accept="acceptTypes"
          class="hidden"
          @change="handleFileSelected"
        />

        <!-- Uploaded/selected media preview -->
        <div v-if="uploadedPreview" class="mb-2.5">
          <div
            class="rounded-lg border border-n-weak overflow-hidden bg-n-solid-3"
          >
            <img
              v-if="headerComponent?.format === 'IMAGE'"
              :src="uploadedPreview.url"
              alt=""
              class="w-full max-h-36 object-cover"
            />
            <div v-else class="flex items-center gap-3 p-3">
              <span
                :class="
                  headerComponent?.format === 'VIDEO'
                    ? 'i-lucide-video'
                    : 'i-lucide-file-text'
                "
                class="size-5 text-n-slate-11"
              />
              <span class="text-sm text-n-slate-12 truncate flex-1">
                {{ uploadedPreview.name }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 mt-1.5">
            <Button
              :label="t('WHATSAPP_TEMPLATES.PARSER.REPLACE_MEDIA')"
              icon="i-lucide-refresh-cw"
              size="xs"
              variant="ghost"
              color="slate"
              @click="triggerFileUpload"
            />
            <Button
              :label="t('WHATSAPP_TEMPLATES.PARSER.REMOVE_MEDIA')"
              icon="i-lucide-trash-2"
              size="xs"
              variant="ghost"
              color="ruby"
              @click="removeUploadedMedia"
            />
          </div>
        </div>

        <!-- Upload button + URL input when no media selected -->
        <div v-else class="space-y-2.5">
          <div class="flex items-center gap-2">
            <Button
              :label="
                isUploading
                  ? t('WHATSAPP_TEMPLATES.PARSER.UPLOADING')
                  : t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_MEDIA')
              "
              :icon="isUploading ? '' : 'i-lucide-upload'"
              size="sm"
              variant="outline"
              color="slate"
              :disabled="isUploading"
              @click="triggerFileUpload"
            >
              <template v-if="isUploading" #icon>
                <Spinner size="small" />
              </template>
            </Button>
            <span class="text-xs text-n-slate-11">
              {{ t('WHATSAPP_TEMPLATES.PARSER.OR_PASTE_URL') }}
            </span>
          </div>
          <div :class="variableFieldRowClass">
            <Input
              :model-value="processedParams.header?.media_url || ''"
              type="url"
              class="min-w-0"
              :placeholder="
                t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                  type: formatType,
                })
              "
              @update:model-value="updateMediaUrl"
            />
            <div :class="variableSelectWrapperClass">
              <select
                :class="variableSelectClass"
                @change="handleVariableSelect($event, 'header')"
              >
                <option value="">
                  {{ t('VARIABLES.DROPDOWN.LABEL', 'Add variable') }}
                </option>
                <optgroup
                  v-for="group in variableOptions"
                  :key="group.label"
                  :label="group.label"
                >
                  <option
                    v-for="opt in group.options"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </optgroup>
              </select>
              <span :class="variableSelectIconClass" />
            </div>
          </div>
        </div>

        <p v-if="uploadError" class="mt-1.5 text-xs text-n-ruby-11">
          {{ uploadError }}
        </p>

        <div v-if="isDocumentTemplate && !uploadedPreview" class="mt-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            hasHeaderVariables
              ? $t('WHATSAPP_TEMPLATES.PARSER.BODY_VARIABLES_LABEL')
              : $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL')
          }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="mb-2.5"
          :class="variableFieldRowClass"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="min-w-0"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
          <div :class="variableSelectWrapperClass">
            <select
              :class="variableSelectClass"
              @change="handleVariableSelect($event, 'body', key)"
            >
              <option value="">
                {{ t('VARIABLES.DROPDOWN.LABEL', 'Add variable') }}
              </option>
              <optgroup
                v-for="group in variableOptions"
                :key="group.label"
                :label="group.label"
              >
                <option
                  v-for="opt in group.options"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </optgroup>
            </select>
            <span :class="variableSelectIconClass" />
          </div>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div
        v-if="processedParams.buttons && processedParams.buttons.some(b => b)"
      >
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <template
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
        >
          <div
            v-if="
              button &&
              !isPaymentButton(button) &&
              button.type !== 'copy_code' &&
              button.type !== 'COPY_CODE'
            "
            class="mb-2.5"
            :class="variableFieldRowClass"
          >
            <Input
              v-model="processedParams.buttons[index].parameter"
              type="text"
              class="min-w-0"
              :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
            />
            <div :class="variableSelectWrapperClass">
              <select
                :class="variableSelectClass"
                @change="handleVariableSelect($event, 'button', index)"
              >
                <option value="">
                  {{ t('VARIABLES.DROPDOWN.LABEL', 'Add variable') }}
                </option>
                <optgroup
                  v-for="group in variableOptions"
                  :key="group.label"
                  :label="group.label"
                >
                  <option
                    v-for="opt in group.options"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </optgroup>
              </select>
              <span :class="variableSelectIconClass" />
            </div>
          </div>

          <div
            v-else-if="
              button &&
              (button.type === 'copy_code' || button.type === 'COPY_CODE')
            "
            class="flex flex-col gap-2.5 mb-2.5 p-3 border border-n-weak rounded-lg bg-n-solid-3"
          >
            <p
              class="text-xs font-medium text-n-slate-12 flex items-center gap-1.5"
            >
              <span class="i-lucide-copy size-4 text-n-slate-11" />
              {{
                t(
                  'WHATSAPP_TEMPLATES.PARSER.COPY_CODE_LABEL',
                  'Código a Copiar'
                )
              }}
            </p>
            <div :class="variableFieldRowClass">
              <Input
                v-model="processedParams.buttons[index].parameter"
                type="text"
                class="min-w-0"
                placeholder="Ex: IMOBI100"
                maxlength="15"
              />
              <div :class="variableSelectWrapperClass">
                <select
                  :class="variableSelectClass"
                  @change="handleVariableSelect($event, 'button', index)"
                >
                  <option value="">
                    {{ t('VARIABLES.DROPDOWN.LABEL', 'Add variable') }}
                  </option>
                  <optgroup
                    v-for="group in variableOptions"
                    :key="group.label"
                    :label="group.label"
                  >
                    <option
                      v-for="opt in group.options"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </optgroup>
                </select>
                <span :class="variableSelectIconClass" />
              </div>
            </div>
          </div>

          <div
            v-else-if="button && isPaymentButton(button)"
            class="flex flex-col gap-2.5 mb-2.5 p-3 border border-n-weak rounded-lg bg-n-solid-3"
          >
            <p
              class="text-xs font-medium text-n-slate-12 flex items-center gap-1.5"
            >
              <span class="i-lucide-receipt size-4 text-n-slate-11" />
              {{ $t('WHATSAPP_TEMPLATES.PARSER.ORDER_DETAILS_LABEL') }}
            </p>
            <select
              v-model="processedParams.buttons[index].payment_method"
              class="input"
            >
              <option
                v-for="method in paymentMethodOptions"
                :key="method.value"
                :value="method.value"
              >
                {{ method.label }}
              </option>
            </select>
            <div
              v-if="
                processedParams.buttons[index].payment_method ===
                'pix_dynamic_code'
              "
              class="grid grid-cols-1 gap-2 md:grid-cols-2"
            >
              <div class="md:col-span-2" :class="variableFieldRowClass">
                <Input
                  v-model="processedParams.buttons[index].pix_code"
                  type="text"
                  class="min-w-0"
                  :placeholder="t('WHATSAPP_TEMPLATES.PARSER.PIX_CODE')"
                />
                <div :class="variableSelectWrapperClass">
                  <select
                    :class="variableSelectClass"
                    @change="handleVariableSelect($event, 'button', index)"
                  >
                    <option value="">
                      {{ t('VARIABLES.DROPDOWN.LABEL', 'Add variable') }}
                    </option>
                    <optgroup
                      v-for="group in variableOptions"
                      :key="group.label"
                      :label="group.label"
                    >
                      <option
                        v-for="opt in group.options"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </optgroup>
                  </select>
                  <span :class="variableSelectIconClass" />
                </div>
              </div>
              <Input
                v-model="processedParams.buttons[index].pix_merchant_name"
                type="text"
                :placeholder="t('WHATSAPP_TEMPLATES.PARSER.PIX_MERCHANT_NAME')"
              />
              <Input
                v-model="processedParams.buttons[index].pix_key"
                type="text"
                :placeholder="t('WHATSAPP_TEMPLATES.PARSER.PIX_KEY')"
              />
              <select
                v-model="processedParams.buttons[index].pix_key_type"
                class="input"
              >
                <option
                  v-for="keyType in PIX_KEY_TYPES"
                  :key="keyType"
                  :value="keyType"
                >
                  {{ keyType }}
                </option>
              </select>
            </div>
            <div v-else :class="variableFieldRowClass">
              <Input
                v-if="
                  processedParams.buttons[index].payment_method === 'boleto'
                "
                v-model="processedParams.buttons[index].boleto_digitable_line"
                type="text"
                class="min-w-0"
                :placeholder="
                  t('WHATSAPP_TEMPLATES.PARSER.BOLETO_DIGITABLE_LINE')
                "
              />
              <Input
                v-else
                v-model="processedParams.buttons[index].payment_link_uri"
                type="url"
                class="min-w-0"
                :placeholder="t('WHATSAPP_TEMPLATES.PARSER.PAYMENT_LINK_URI')"
              />
              <div :class="variableSelectWrapperClass">
                <select
                  :class="variableSelectClass"
                  @change="handleVariableSelect($event, 'button', index)"
                >
                  <option value="">
                    {{ t('VARIABLES.DROPDOWN.LABEL', 'Add variable') }}
                  </option>
                  <optgroup
                    v-for="group in variableOptions"
                    :key="group.label"
                    :label="group.label"
                  >
                    <option
                      v-for="opt in group.options"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </optgroup>
                </select>
                <span :class="variableSelectIconClass" />
              </div>
            </div>
            <div v-if="button.type === 'order_details'" class="flex gap-2">
              <Input
                v-model="processedParams.buttons[index].reference_id"
                type="text"
                class="flex-1"
                :placeholder="t('WHATSAPP_TEMPLATES.PARSER.REFERENCE_ID')"
              />
              <Input
                v-model="processedParams.buttons[index].total_amount"
                type="number"
                class="flex-1"
                :placeholder="t('WHATSAPP_TEMPLATES.PARSER.TOTAL_AMOUNT')"
              />
            </div>
          </div>
        </template>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
